import { AfterViewInit, Directive, ElementRef, HostListener, Renderer2 } from '@angular/core';

@Directive({
    selector: '[shDynamicResize]',
    standalone: true
})
export class ShDynamicResizeDirective implements AfterViewInit {
    private mirrorSpan: HTMLElement;
    private maxWidthPx: number = Infinity;
    private readonly lineHeight: number = 1;
    private padding: number = 0;

    constructor(
        private readonly el: ElementRef,
        private readonly renderer: Renderer2
    ) {}

    ngAfterViewInit() {
        this.createMirror();
        this.calculateMaxWidth();
        this.updateWidth();
    }

    @HostListener('input')
    onInput() {
        this.updateWidth();
    }

    @HostListener('focus')
    onFocus() {
        this.updateWidth();
    }

    private createMirror() {
        const mirror: HTMLElement | null = document.getElementById('mirror');
        if (mirror) {
            this.mirrorSpan = mirror;
        } else {
            this.mirrorSpan = this.renderer.createElement('span');
        }

        this.renderer.setAttribute(this.mirrorSpan, 'id', 'mirror');
        this.renderer.setStyle(this.mirrorSpan, 'position', 'absolute');
        this.renderer.setStyle(this.mirrorSpan, 'top', '-999999px');
        this.renderer.setStyle(this.mirrorSpan, 'left', '-999999px');
        this.renderer.setStyle(this.mirrorSpan, 'white-space', 'pre');
        this.renderer.setStyle(this.mirrorSpan, 'white-space', 'pre');

        // Copy font styles from input to mirror
        const styles: CSSStyleDeclaration = getComputedStyle(this.el.nativeElement);
        const propsToCopy: string[] = [
            'font-size',
            'font-family',
            'font-weight',
            'fontstyle',
            'letter-spacing',
            'text-transform',
            'padding',
            'border',
            'margin',
            'font-variant',
            'line-height',
            'word-spacing',
            'text-indent',
            'text-rendering',
            '-webkit-font-smoothing',
            '-moz-osx-font-smoothing'
        ];

        propsToCopy.forEach(prop => {
            this.renderer.setStyle(this.mirrorSpan, prop, styles.getPropertyValue(prop));
        });

        document.body.appendChild(this.mirrorSpan);
    }

    private calculateMaxWidth(): void {
        const input: HTMLElement = this.el.nativeElement;
        const styles: CSSStyleDeclaration = getComputedStyle(input);
        const maxWidthStr: string = styles.getPropertyValue('max-width').trim();
        this.padding =
            Number.parseFloat(styles.getPropertyValue('padding-left')) +
            Number.parseFloat(styles.getPropertyValue('padding-right'));

        if (maxWidthStr && maxWidthStr !== 'none') {
            if (maxWidthStr.endsWith('%')) {
                const percent: number = Number.parseFloat(maxWidthStr);
                const parent: HTMLElement = input.offsetParent as HTMLElement;
                if (parent) {
                    const parentStyles: CSSStyleDeclaration = getComputedStyle(parent);
                    const parentWidth: number =
                        parent.clientWidth -
                        Number.parseFloat(parentStyles.paddingLeft) -
                        Number.parseFloat(parentStyles.paddingRight);
                    this.maxWidthPx = (percent / 100) * parentWidth;
                }
            } else if (maxWidthStr.endsWith('px')) {
                this.maxWidthPx = Number.parseFloat(maxWidthStr);
            }
        }
    }

    private updateWidth(): void {
        const input = this.el.nativeElement;
        const value = input.value ?? input.placeholder ?? '';
        this.mirrorSpan.textContent = value;

        // Add some extra spacing to prevent input from feeling cramped
        const newWidth = Math.ceil(this.mirrorSpan.offsetWidth) + this.padding + 2;

        if (newWidth < this.maxWidthPx) {
            this.renderer.setStyle(input, 'width', `${newWidth}px`);
        }
    }
}
